/**
 * THE SHRINK REFUSAL. An edit must never destroy more than it can rebuild
 * without saying so first.
 *
 * Bobbie, 2026-09-03, after a day of comp dances disappearing when she edited
 * them: "even when I make an edit it does not change it, it doesn't show up on
 * the calendar... it's affecting people not knowing when they're supposed to
 * be in class and people missing classes."
 *
 * applyClassSeriesScheduleEdit cancels EVERY future booking and then rebooks
 * from the new rule, and the rebook is bounded by the A/B weeks a Director has
 * marked SO FAR. Measured on her real lists with the real engine: a B-week
 * class running Sep 7 to May 27 can book seven nights, while the same class on
 * every week books thirty-eight. So editing a comp dance in September cancelled
 * its whole year and rebooked seven, destroying January through May silently
 * and reporting it afterwards in the past tense.
 *
 * The existing calendar guard above only fires when a letter list is entirely
 * EMPTY (lettersMissingFromCalendar). A list that is merely SHORT sails
 * through it. That guard was written for the half-filled calendar and never
 * considered the short one, which is the ordinary state of a studio that sets
 * its weeks a term at a time and is exactly how Bobbie works.
 *
 * This compares what the edit would rebook against what it is about to cancel
 * and refuses when the answer is materially smaller, BEFORE releasing anything.
 * Both numbers were already computed for the destination preflight; nothing new
 * is read to know this.
 *
 * ONLY FIRES WHEN WEEKS CARRY NO LETTER YET. A class legitimately meets fewer
 * times for all sorts of reasons: it moved to the letter with fewer weeks left
 * in the term, a closed day landed on it, a Director cancelled a night by hand.
 * Refusing those would nag about every honest edit and teach her to click
 * through the warning, which is worse than no warning at all. Losing weeks
 * because Studio Settings has not been told their letter yet is different: it
 * is recoverable the moment she marks them, and it is the one that was
 * happening silently. So the message always has a real unmarked count to name.
 */
export function scheduleEditShrinkMessage(
  cancelling: number,
  rebooking: number,
  unmarkedWeeks: number
): string {
  const lost = cancelling - rebooking;
  return (
    `This edit would take ${lost} ${lost === 1 ? 'session' : 'sessions'} off the calendar and not put `
    + `${lost === 1 ? 'it' : 'them'} back: ${cancelling} upcoming ${cancelling === 1 ? 'session is' : 'sessions are'} booked now, `
    + `and the new time can only book ${rebooking}. Nothing was changed and not one booking was released. `
    + `${unmarkedWeeks} of the weeks this class would meet are not marked A or B yet in Studio Settings, which is why. `
    + 'Mark those weeks under "Week A and Week B list" and save this time again, and the class keeps its whole year. '
    + 'If you really do mean to shorten this class, save it again and confirm.'
  );
}

/**
 * How much smaller an edit is allowed to leave a class before it has to ask.
 * One session is ordinary (a term boundary, a closed day, a week that has just
 * started); losing a run of them is the failure this guard exists to catch.
 */
export const SCHEDULE_EDIT_SHRINK_TOLERANCE = 1;
